import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Calendar, MapPin, Search, SlidersHorizontal } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Slider } from "@/components/ui/slider";

type SearchFormProps = {
  initialLocation?: string;
};

export default function SearchForm({ initialLocation = "" }: SearchFormProps) {
  const [searchLocation, setSearchLocation] = useState(initialLocation);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const startDateRef = useRef<HTMLInputElement>(null);
  const endDateRef = useRef<HTMLInputElement>(null);
  const [priceRange, setPriceRange] = useState([0, 500]);
  const [selectedSizes, setSelectedSizes] = useState<string[]>([]);
  const [facilities, setFacilities] = useState({
    charger: false,
    covered: false,
    camera: false,
  });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setSearchLocation(initialLocation);
  }, [initialLocation]);

  const formatDateLabel = (date: string, fallback: string) => {
    if (!date) return fallback;
    return new Intl.DateTimeFormat("da-DK", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(new Date(date));
  };

  const openDatePicker = (input: HTMLInputElement | null) => {
    if (!input) return;
    input.showPicker?.();
    input.focus();
  };

  const handleSearch = (e?: React.FormEvent) => {
    e?.preventDefault();

    let from = startDate.trim();
    let to = endDate.trim();
    if (from && !to) to = from;
    if (to && !from) from = to;

    const params = new URLSearchParams();
    if (searchLocation.trim()) params.set("query", searchLocation.trim());
    if (from) params.set("from", from);
    if (to) params.set("to", to);
    
    // Add filters
    if (priceRange[0] > 0 || priceRange[1] < 500) {
      params.set("minPrice", priceRange[0].toString());
      params.set("maxPrice", priceRange[1].toString());
    }
    if (selectedSizes.length > 0) {
      params.set("sizes", selectedSizes.join(","));
    }
    if (facilities.charger) params.set("charger", "true");
    if (facilities.covered) params.set("covered", "true");
    if (facilities.camera) params.set("camera", "true");

    navigate(`/search?${params.toString()}`);
  };

  return (
    <form onSubmit={handleSearch} className="w-full max-w-4xl mx-auto">
      <div className="flex flex-col md:flex-row gap-2 p-2 bg-card rounded-2xl shadow-lg border">
        {/* Location Input */}
        <div className="flex-1 min-w-0">
          <div className="relative">
            <MapPin className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-muted-foreground" />
            <span className="absolute left-10 top-2 text-[11px] font-medium uppercase text-muted-foreground">Område</span>
            <Input
              name="query"
              type="text"
              placeholder="Hvor skal du parkere?"
              value={searchLocation}
              onChange={(e) => setSearchLocation(e.target.value)}
              className="h-14 border-0 bg-transparent pb-2 pl-10 pt-6 text-base text-foreground focus-visible:ring-0"
            />
          </div>
        </div>

        <div className="h-px md:h-auto md:w-px bg-border" />

        {/* Date Inputs */}
        <div className="flex flex-1 flex-col gap-2 sm:flex-row">
          <div className="flex-1 min-w-0">
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-muted-foreground" />
              <span className="absolute left-10 top-2 text-[11px] font-medium uppercase text-muted-foreground">Fra</span>
              <button
                type="button"
                onClick={() => openDatePicker(startDateRef.current)}
                className="block h-14 w-full rounded-md pb-2 pl-10 pr-2 pt-6 text-left text-sm font-medium text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:text-base"
              >
                {formatDateLabel(startDate, "Vælg dato")}
              </button>
              <Input
                ref={startDateRef}
                name="from"
                type="date"
                aria-label="Startdato"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="pointer-events-none absolute inset-0 h-14 border-0 bg-transparent opacity-0"
              />
            </div>
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-muted-foreground" />
              <span className="absolute left-10 top-2 text-[11px] font-medium uppercase text-muted-foreground">Til</span>
              <button
                type="button"
                onClick={() => openDatePicker(endDateRef.current)}
                className="block h-14 w-full rounded-md pb-2 pl-10 pr-2 pt-6 text-left text-sm font-medium text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:text-base"
              >
                {formatDateLabel(endDate, "Vælg dato")}
              </button>
              <Input
                ref={endDateRef}
                name="to"
                type="date"
                aria-label="Slutdato"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="pointer-events-none absolute inset-0 h-14 border-0 bg-transparent opacity-0"
              />
            </div>
          </div>
        </div>

        {/* Filters Button */}
        <Dialog open={filtersOpen} onOpenChange={setFiltersOpen}>
          <DialogTrigger asChild>
            <Button type="button" variant="outline" size="lg" className="h-14 w-full gap-2 text-foreground md:w-auto md:px-5 [&_svg]:text-foreground">
              <SlidersHorizontal className="h-4 w-4" />
              <span>Filtre</span>
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Filtrer søgning</DialogTitle>
              <DialogDescription>
                Tilpas dine søgefiltre for at finde den perfekte parkeringsplads
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-6 py-4">
              <div>
                <Label className="text-base font-semibold">Pris per dag (kr)</Label>
                <div className="pt-2">
                  <Slider
                    value={priceRange}
                    onValueChange={setPriceRange}
                    min={0}
                    max={500}
                    step={10}
                  />
                  <div className="flex justify-between mt-2 text-sm text-muted-foreground">
                    <span>{priceRange[0]} kr</span>
                    <span>{priceRange[1] === 500 ? "500+ kr" : `${priceRange[1]} kr`}</span>
                  </div>
                </div>
              </div>

              <div>
                <Label className="text-base font-semibold mb-3 block">Størrelse</Label>
                <div className="space-y-2">
                  {[
                    { id: "S", label: "Lille (< 4.2 m)", desc: "Smart, Fiat 500" },
                    { id: "M", label: "Medium (4.2–4.8 m)", desc: "VW Golf, Toyota Corolla" },
                    { id: "L", label: "Stor (4.8–5.2 m)", desc: "BMW 5-serie, Volvo XC90" },
                    { id: "XL", label: "Ekstra stor (> 5.2 m)", desc: "Varevogn, stor SUV" },
                  ].map(({ id, label, desc }) => (
                    <div key={id} className="flex items-start space-x-2">
                      <Checkbox
                        id={`filter-${id}`}
                        checked={selectedSizes.includes(id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedSizes([...selectedSizes, id]);
                          } else {
                            setSelectedSizes(selectedSizes.filter((s) => s !== id));
                          }
                        }}
                      />
                      <div className="grid gap-0.5 leading-none">
                        <label htmlFor={`filter-${id}`} className="text-sm font-medium cursor-pointer">
                          {label}
                        </label>
                        <p className="text-xs text-muted-foreground">{desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-base font-semibold mb-3 block">Faciliteter</Label>
                <div className="space-y-2">
                  <div className="flex items-start space-x-2">
                    <Checkbox
                      id="filter-charger"
                      checked={facilities.charger}
                      onCheckedChange={(checked) =>
                        setFacilities({ ...facilities, charger: !!checked })
                      }
                    />
                    <div className="grid gap-0.5 leading-none">
                      <label htmlFor="filter-charger" className="text-sm font-medium cursor-pointer">
                        El-lader
                      </label>
                      <p className="text-xs text-muted-foreground">Opladning til elbiler</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-2">
                    <Checkbox
                      id="filter-covered"
                      checked={facilities.covered}
                      onCheckedChange={(checked) =>
                        setFacilities({ ...facilities, covered: !!checked })
                      }
                    />
                    <div className="grid gap-0.5 leading-none">
                      <label htmlFor="filter-covered" className="text-sm font-medium cursor-pointer">
                        Overdækket
                      </label>
                      <p className="text-xs text-muted-foreground">Beskyttelse mod vejr</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-2">
                    <Checkbox
                      id="filter-camera"
                      checked={facilities.camera}
                      onCheckedChange={(checked) =>
                        setFacilities({ ...facilities, camera: !!checked })
                      }
                    />
                    <div className="grid gap-0.5 leading-none">
                      <label htmlFor="filter-camera" className="text-sm font-medium cursor-pointer">
                        Kamera
                      </label>
                      <p className="text-xs text-muted-foreground">Ekstra sikkerhed</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  setPriceRange([0, 500]);
                  setSelectedSizes([]);
                  setFacilities({ charger: false, covered: false, camera: false });
                }}
              >
                Nulstil
              </Button>
              <Button 
                type="button" 
                onClick={() => {
                  handleSearch();
                  setTimeout(() => setFiltersOpen(false), 100);
                }}
              >
                Anvend filtre
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Search Button */}
        <Button type="submit" size="lg" className="h-14 w-full md:w-auto md:px-8">
          <Search className="h-4 w-4 md:mr-2" />
          <span className="hidden md:inline">Søg</span>
        </Button>
      </div>
    </form>
  );
}
